import { compulsoryColumns, excludedColumns } from "@Components/FileColumnsTreeView/constants";
import axiosWrapper from "@AxiosWrapper";
import { getBackendUrl } from "@Utils";
import type { GetDistinctFilePropertyNamesResponse } from "@ServerTypes/queries/queries.schema";

/** A grid column referenced either by its plain field name or by a definition that carries one. */
export type ColumnRef = string | { field?: unknown };

/** Any non-null object, read as an open property bag. */
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/** A nested property bag: objects only, arrays excluded. */
const isPlainRecord = (value: unknown): value is Record<string, unknown> => isRecord(value) && !Array.isArray(value);

/**
 * SCHEMA GAP: `getDistinctFilePropertyNamesResponse` declares the names under `result`, but
 * queries.service returns them under an undeclared `data` key. Read whichever key is present and
 * keep only the string entries, so this survives both the current server and a corrected schema.
 */
const readDistinctPropertyNames = (payload: unknown): string[] => {
  if (!isRecord(payload)) return [];
  const names = "data" in payload ? payload.data : payload.result;
  return Array.isArray(names) ? names.filter((name): name is string => typeof name === "string") : [];
};

/**
 * Drops the excluded and compulsory columns. Generic because callers pass either the plain field
 * names (`string[]`) or `{ field, label }` pairs, and each expects its own element type back —
 * `SearchSet1/index.tsx` feeds the filtered field names straight into a `string[]` prop.
 */
export const getFilteredColumns = <T extends ColumnRef>(columns: T[]): T[] =>
  columns.filter((col: ColumnRef) => {
    // `col` is either the field name itself or a column definition holding it.
    const column = typeof col === "string" ? col : col.field;
    return (
      !excludedColumns.some((excludedColumn) => excludedColumn.field === column) &&
      !compulsoryColumns.some((compulsoryColumn) => compulsoryColumn.field === column)
    );
  });

export const getFilePropertyNames = async (fileVersionId: string | string[]) => {
  const propertyNames = await axiosWrapper.post<GetDistinctFilePropertyNamesResponse>(
    `${getBackendUrl()}/queries/getDistinctFilePropertyNames`,
    {
      fileVersionId: Array.isArray(fileVersionId) ? fileVersionId : [fileVersionId],
    },
  );

  let columns: string[] = [];
  if (propertyNames.data.status) {
    columns = getFilteredColumns(readDistinctPropertyNames(propertyNames.data));
  }
  return [columns, propertyNames.data];
};

export const convertToDotNotation = (
  obj: Record<string, unknown>,
  parentKey = "",
  result: Record<string, unknown> = {},
) => {
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      const newKey = parentKey ? `${parentKey}.${key}` : key;
      const value = obj[key];

      // Nested property bags are flattened into dotted keys, except under `customProperties`,
      // whose entries are keyed by opaque form-field ids and are kept whole.
      if (isPlainRecord(value) && parentKey != "customProperties") {
        convertToDotNotation(value, newKey, result);
      } else {
        result[newKey] = value;
      }
    }
  }
  return result;
};

/**
 * Flattens the merged model-properties map `{ [fileVersionId]: { [nodeId]: node } }` to grid rows.
 * Callers hold that map under several node types (ModelPropertyNode, MergedNode), so the per-file
 * bucket is only `object` here and each node is read as an open record.
 */
export const getModifiedNodesList = (modelProperties: Record<string, object>): Record<string, unknown>[] => {
  // The per-file bucket is only `object`, so `Object.values` on it widens to `any[]`. Take the
  // nodes as `unknown` and re-check each one instead, so nothing untyped leaks into the rows.
  const nodes: unknown[] = Object.values(modelProperties).flatMap((bucket) => Object.values(bucket));
  return nodes.map((node) => {
    // A non-object node would previously have produced an `undefined_undefined` row rather than
    // throwing; keep that shape.
    const data: Record<string, unknown> = isRecord(node) ? node : {};
    return {
      id: `${String(data.fileVersionId)}_${String(data.nodeId)}`,
      ...convertToDotNotation(data),
    };
  });
};

export const extractPropertyPartialName = (field: string | null | undefined): string => {
  // The name is genuinely optional: an incomplete query rule (no field selected)
  // has a null field, so there is no partial name to extract.
  if (!field) {
    return "";
  }
  const rootProperty = field.split(".");
  const value = rootProperty.slice(1).join(".").split("/");
  if (value.length > 1) {
    return value.slice(1).join("/");
  }
  // split() always yields at least one element, but noUncheckedIndexedAccess types
  // value[0] as string | undefined; coalesce so the return type stays a plain string.
  return value[0] ?? "";
};
